/**
 * Staff Selection Modal Component
 * 
 * Modal component for selecting staff to add to a department.
 * Inspired by ListModal.js but with checkbox selection functionality like DepartmentAllocation.js.
 * Shows only staff that belong to the facility but are not currently in the selected department.
 * 
 * @component
 */

import React, { useState, useEffect } from 'react';
import { Typography, message } from 'antd';
import { Box, Checkbox } from '@mui/material';
import { Search, Button } from '@voicecare.ai/ui-library';
import { useTheme } from '../../ThemeContext';
import DepartmentService from '../../services/department-service';
import LogoLoader from '../common/loaders/gif-logo-loader';

const { Title, Text } = Typography;

/**
 * Staff Selection Modal
 * 
 * @param {boolean} open - Controls modal visibility
 * @param {string} departmentId - ID of the department to add staff to
 * @param {string} facilityId - ID of the facility to load staff from
 * @param {string} departmentName - Name of the department (for display)
 * @param {string} facilityName - Name of the facility (for display)
 * @param {string[]} currentStaffIds - Array of staff IDs already in the department
 * @param {Function} onClose - Function to close the modal
 * @param {Function} onSave - Function called after successful save with updated department data
 */
const StaffSelectionModal = ({
  open,
  departmentId,
  facilityId,
  departmentName = 'Unknown Department',
  facilityName = 'Unknown Facility',
  currentStaffIds = [],
  onClose,
  onSave
}) => {
  const { themeMode, currentTheme } = useTheme();
  const brandOrange = '#e63e2b';

  // State management
  const [facilityStaff, setFacilityStaff] = useState([]);
  const [selectedStaffIds, setSelectedStaffIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load facility staff when modal opens
  useEffect(() => {
    if (open && facilityId) {
      loadFacilityStaff();
    }
  }, [open, facilityId]);

  /**
   * Loads all staff for the facility
   */
  const loadFacilityStaff = async () => {
    try {
      setLoading(true);
      console.log('🏢 Loading facility staff for selection modal:', facilityId);
      
      const result = await DepartmentService.getStaffByFacility(facilityId);
      
      if (result.success) {
        setFacilityStaff(result.data);
        console.log('✅ Facility staff loaded for modal:', result.data.length);
      } else {
        console.warn('⚠️ Failed to load facility staff:', result.message);
        message.error(result.message || 'Failed to load facility staff');
        setFacilityStaff([]);
      }
    } catch (error) {
      console.error('❌ Error loading facility staff:', error);
      message.error('Failed to load facility staff. Please try again.');
      setFacilityStaff([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Filters staff to show only those not currently in the department
   * and matches search query
   */
  const availableStaff = facilityStaff
    .filter(staff => {
      // Only show staff not currently in the department
      return !currentStaffIds.includes(staff._id);
    })
    .filter(staff => {
      // Apply search filter
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        staff.name?.toLowerCase().includes(query) ||
        staff.email?.toLowerCase().includes(query)
      );
    })
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  /**
   * Handles staff checkbox change
   */
  const handleStaffCheckboxChange = (staffId, checked) => {
    setSelectedStaffIds(prev => {
      if (checked) {
        return [...prev, staffId];
      } else {
        return prev.filter(id => id !== staffId);
      }
    });
  };

  /**
   * Handles save button click
   */
  const handleSave = async () => {
    if (selectedStaffIds.length === 0) {
      message.error('Please select at least one staff member');
      return;
    }

    try {
      setSaving(true);
      console.log('💾 Saving staff selection:', { departmentId, selectedStaffIds });
      
      const result = await DepartmentService.addStaffToDepartment(departmentId, selectedStaffIds);
      
      if (result.success) {
        message.success(result.message || 'Staff added successfully');
        onSave && onSave(); // Trigger parent refresh
        handleClose(); // Close modal
      } else {
        message.error(result.message || 'Failed to add staff');
      }
    } catch (error) {
      console.error('❌ Error saving staff:', error);
      message.error('Failed to add staff. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Handles modal close
   */
  const handleClose = () => {
    setSelectedStaffIds([]);
    setSearchQuery('');
    onClose();
  };

  if (!open) return null;

  // Theme-based styling inspired by ListModal and BillingRestrictionPopup
  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10000,
    animation: 'fadeIn 0.3s ease-out'
  };

  const modalStyle = {
    backgroundColor: themeMode === 'dark' ? '#04002b' : '#ffffff',
    color: currentTheme.text?.primary || currentTheme.homePage?.textColor,
    borderRadius: '16px',
    padding: '0',
    maxWidth: '600px',
    width: '90%',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: themeMode === 'dark' 
      ? '0 20px 40px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.1)'
      : '0 20px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)',
    border: themeMode === 'dark' ? '1px solid #302d4c' : '1px solid #e9e8ea',
    animation: 'slideIn 0.3s ease-out',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif",
  };

  const headerStyle = {
    padding: '24px 24px 16px 24px',
    borderBottom: `1px solid ${themeMode === 'dark' ? '#302d4c' : '#e9e8ea'}`,
    backgroundColor: themeMode === 'dark' ? '#04002b' : '#ffffff',
    position: 'sticky',
    top: 0,
    zIndex: 1,
    flexShrink: 0
  };

  const titleRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '8px'
  };

  const titleStyle = {
    fontSize: '20px',
    fontWeight: '600',
    color: currentTheme.text?.primary || currentTheme.homePage?.textColor,
    margin: '0',
    lineHeight: '1.3'
  };

  const subtitleStyle = {
    fontSize: '14px',
    fontWeight: '400',
    color: themeMode === 'dark' ? '#a0a0a0' : '#666666',
    margin: '0',
    opacity: 0.8
  };

  const closeButtonStyle = {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    color: currentTheme.text?.primary || currentTheme.homePage?.textColor,
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '4px',
    opacity: 0.7,
    transition: 'opacity 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    flexShrink: 0
  };

  const contentStyle = {
    flex: 1,
    overflow: 'auto',
    minHeight: 0,
    padding: '16px 24px'
  };

  const footerStyle = {
    padding: '16px 24px',
    borderTop: `1px solid ${themeMode === 'dark' ? '#302d4c' : '#e9e8ea'}`,
    backgroundColor: themeMode === 'dark' ? '#04002b' : '#ffffff',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    flexShrink: 0
  };

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideIn {
            from { 
              opacity: 0; 
              transform: translateY(-20px) scale(0.95); 
            }
            to { 
              opacity: 1; 
              transform: translateY(0) scale(1); 
            }
          }
        `}
      </style>
      <div style={overlayStyle} onClick={handleClose}>
        <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div style={headerStyle}>
            <div style={titleRowStyle}>
              <div>
                <h2 style={titleStyle}>Add Staff to Department</h2>
                <p style={subtitleStyle}>
                  {departmentName} • {facilityName}
                </p>
              </div>
              <button 
                style={closeButtonStyle}
                onClick={handleClose}
                onMouseEnter={(e) => e.target.style.opacity = '1'}
                onMouseLeave={(e) => e.target.style.opacity = '0.7'}
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
          </div>
          
          {/* Content */}
          <div style={contentStyle}>
            {/* Search */}
            <div style={{ marginBottom: '16px' }}>
              <Search
                value={searchQuery}
                onChange={setSearchQuery}
                themeMode={themeMode}
                currentTheme={currentTheme}
                style={{ width: '92%' }}
                placeholder="Search staff by name or email..."
                fontSize="0.875rem"
              />
            </div>

            {/* Staff List */}
            {loading ? (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '200px',
                flexDirection: 'column',
                gap: '1rem'
              }}>
                <div style={{ 
                  width: '60px', 
                  height: '60px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <LogoLoader />
                </div>
                <Text style={{
                  fontSize: '0.875rem',
                  color: themeMode === 'dark' ? '#ffffff' : '#666666',
                  textAlign: 'center'
                }}>
                  Loading facility staff...
                </Text>
              </div>
            ) : availableStaff.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '48px 24px',
                color: themeMode === 'dark' ? '#a0a0a0' : '#666666',
                fontSize: '14px'
              }}>
                {searchQuery 
                  ? 'No staff found matching your search'
                  : 'No available staff to add (all staff are already in this department)'
                }
              </div>
            ) : (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                maxHeight: '300px',
                overflowY: 'auto'
              }}>
                {availableStaff.map((staff, index) => {
                  const isSelected = selectedStaffIds.includes(staff._id);
                  
                  return (
                    <div
                      key={staff._id || index}
                      style={{
                        backgroundColor: themeMode === 'dark' ? '#302d4c' : '#f8f9fa',
                        border: `1px solid ${
                          isSelected
                            ? brandOrange
                            : themeMode === 'dark'
                            ? '#404040'
                            : '#e9e8ea'
                        }`,
                        borderRadius: '8px',
                        padding: '12px',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <Text
                            strong
                            style={{
                              fontSize: '14px',
                              color: themeMode === 'dark' ? '#ffffff' : '#000000',
                              display: 'block',
                              marginBottom: '4px'
                            }}
                          >
                            {staff.name || 'Unknown Staff'}
                          </Text>
                          {staff.email && (
                            <Text
                              style={{
                                fontSize: '12px',
                                color: themeMode === 'dark' ? '#a0a0a0' : '#666666',
                                display: 'block'
                              }}
                            >
                              {staff.email}
                            </Text>
                          )}
                        </div>
                        <Checkbox
                          size="medium"
                          checked={isSelected}
                          onChange={(e) => handleStaffCheckboxChange(staff._id, e.target.checked)}
                          inputProps={{ 'aria-label': 'select staff' }}
                          sx={{
                            color: brandOrange,
                            '&.Mui-checked': { color: brandOrange },
                            '&.MuiCheckbox-indeterminate': { color: brandOrange },
                            '& .MuiSvgIcon-root': { fontSize: 20 },
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={footerStyle}>
            <Button
              variant="outlined"
              onClick={handleClose}
              label="Cancel"
              style={{ borderRadius: "6px" }}
              disabled={saving}
            />
            <Button
              variant="contained"
              onClick={handleSave}
              label={saving ? 'Adding...' : `Add ${selectedStaffIds.length} Staff`}
              style={{ borderRadius: "6px" }}
              disabled={selectedStaffIds.length === 0 || saving}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default StaffSelectionModal;
